import React from 'react';
import WhatsAppNumberSelect from 'components/WhatsAppNumberSelect';
import Loader from 'components/commonComponents/Loader/Index';
import moment from 'moment';

/**
 * BusinessDetailsCard Component
 *
 * Displays comprehensive WhatsApp Business Account details including:
 * - Account name and connection status
 * - Plan information and expiry
 * - Quality rating and credits (hidden for test accounts)
 * - Messaging limits (hidden for test accounts)
 * - WhatsApp number selector
 *
 * @param {Object} props - Component properties
 * @param {string} props.whatsappBusinessAccountName - Business account name from API root
 * @param {string} props.wabaStatus - WABA status from API root (e.g., "VERIFIED", "PENDING")
 * @param {Object} props.selectedNumber - Currently selected WhatsApp number object
 * @param {Array} props.whatsappNumbers - List of available WhatsApp numbers
 * @param {Function} props.onSelectNumber - Callback when number is selected
 * @param {boolean} props.loadingNumbers - Loading state for WhatsApp numbers
 * @param {Object} props.planDetails - Plan information (plan_name, end_date, etc.)
 * @param {Object} props.brandCredits - Brand credits information (total_amount, currency_symbol)
 * @param {Object} props.messagingLimitData - Messaging limit data (limit, remaining)
 * @param {Object} props.selectedQuality - Quality rating object with label and color
 * @param {boolean} props.isTestAccount - Whether this is a test account
 * @param {Object} props.brandModeDetails - Test account details (created_at for expiry calculation)
 */
const BusinessDetailsCard = ({
  whatsappBusinessAccountName,
  wabaStatus,
  selectedNumber,
  whatsappNumbers,
  onSelectNumber,
  loadingNumbers,
  planDetails,
  brandCredits,
  messagingLimitData,
  selectedQuality,
  isTestAccount = false,
  brandModeDetails,
}) => {
  /**
   * Calculate remaining days until test account expiry
   * Test accounts expire 7 days after creation
   */
  const calculateTestAccountExpiry = () => {
    if (!brandModeDetails?.created_at) return null;

    const createdDate = moment(brandModeDetails.created_at);
    const expiryDate = createdDate.clone().add(7, 'days');
    const today = moment();
    const daysLeft = expiryDate.diff(today, 'days');

    if (daysLeft < 0) {
      return { label: 'Expired', color: '#F96056', daysLeft: 0 };
    } else if (daysLeft === 0) {
      return { label: 'Expires today', color: '#F6A723', daysLeft: 0 };
    } else if (daysLeft <= 2) {
      return {
        label: `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`,
        color: '#F6A723',
        daysLeft,
      };
    } else {
      return { label: `${daysLeft} days left`, color: '#0ACD95', daysLeft };
    }
  };

  /**
   * Format date to DD/MM/YY format
   */
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const date = moment(dateString);
    return date.isValid() ? date.format('DD/MM/YY') : '--';
  };

  /**
   * Format number with thousand separators
   */
  const formatNumber = (value) => {
    if (value === 0) return '0';
    if (value === undefined || value === null || value === '') return '--';
    if (typeof value === 'string' && value.toUpperCase() === 'UNLIMITED') return 'Unlimited';
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return value;
    return parsed.toLocaleString();
  };

  // Get business account name from top-level API response
  const businessAccountName = isTestAccount
    ? 'Test Account Connected'
    : whatsappBusinessAccountName || '--';

  // Get WABA status from API root (only show if not test account)
  const displayWabaStatus = !isTestAccount && wabaStatus ? wabaStatus : null;

  // Get plan name
  const planName =
    planDetails?.plan_code || (planDetails?.is_trial ? 'Free Trial' : 'Free');

  // Calculate test account expiry info (must be before planEndsOn)
  const testAccountExpiry = isTestAccount ? calculateTestAccountExpiry() : null;

  // Get plan end date or test account expiry
  const planEndsOn =
    isTestAccount && testAccountExpiry
      ? testAccountExpiry.label
      : formatDate(planDetails?.end_date);

  // Get credits left
  const creditsLeft = brandCredits?.total_amount
    ? `${brandCredits.currency_symbol || ''}${formatNumber(
        brandCredits.total_amount,
      )}`
    : '--';

  // Get messaging limit
  const isUnlimited = messagingLimitData?.limit && 
    (typeof messagingLimitData.limit === 'string' && messagingLimitData.limit.toUpperCase() === 'UNLIMITED');
  
  const messagingLimit = messagingLimitData
    ? isUnlimited
      ? 'Unlimited'
      : `${formatNumber(messagingLimitData.remaining)}/${formatNumber(
          messagingLimitData.limit,
        )}`
    : '--';

  return (
    <div className="col-span-12 lg:col-span-4 bg-[var(--surface)] rounded-2xl p-6 shadow-[0px_20px_50px_-32px_rgba(15,23,42,0.35)] border border-[var(--surface-border)] flex flex-col gap-4 h-full">
      <div className="flex-1 flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[12px] uppercase tracking-wide text-[var(--font-600)] mb-1">
              Whatsapp Business account
            </p>
            <p className="text-[18px] font-semibold text-[var(--textBlack)] leading-6">
              {businessAccountName}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {displayWabaStatus && (
              <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#ecfdf3] px-3 py-1 text-xs text-[#15803d]">
                <span className="inline-block w-2 h-2 rounded-full bg-[#22c55e]" />
                {displayWabaStatus}
              </div>
            )}
          </div>
          {isTestAccount && testAccountExpiry ? (
            <span
              className="text-xs font-semibold px-3 py-1 rounded-full"
              style={{
                background: `${testAccountExpiry.color}1A`,
                color: testAccountExpiry.color,
              }}
            >
              {testAccountExpiry.label}
            </span>
          ) : null}
        </div>

        {/* Plan and Account Details Grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <div className="rounded-xl bg-[var(--surface-muted)] px-3 py-2">
            <p className="text-[var(--font-600)] mb-1 text-xs">Plan</p>
            <p className="text-[15px] font-semibold text-[var(--textBlack)] capitalize">
              {planName}
            </p>
          </div>

          <div className="rounded-xl bg-[var(--surface-muted)] px-3 py-2">
            <p className="text-[var(--font-600)] mb-1 text-xs">Ends on</p>
            <p className="text-[15px] font-semibold text-[var(--textBlack)]">
              {planEndsOn}
            </p>
          </div>

          {!isTestAccount && (
            <div className="rounded-xl bg-[var(--surface-muted)] px-3 py-2">
              <p className="text-[var(--font-600)] mb-1 text-xs">Quality</p>
              <div className="flex items-center gap-2">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: selectedQuality.color }}
                ></span>
                <span className="text-[15px] font-semibold text-[var(--textBlack)]">
                  {selectedQuality.label}
                </span>
              </div>
            </div>
          )}

          <div className="rounded-xl bg-[var(--surface-muted)] px-3 py-2">
            <p className="text-[var(--font-600)] mb-1 text-xs">Credits left</p>
            <p className="text-[15px] font-semibold text-[var(--textBlack)]">
              {creditsLeft}
            </p>
          </div>

          {!isTestAccount && (
            <div className="col-span-2 rounded-xl bg-[var(--surface-muted)] px-3 py-2">
              <div className="flex items-center justify-between mb-2 text-xs text-[var(--font-600)]">
                <span>Messaging limit</span>
                <span className="text-[12px] font-semibold text-[var(--textBlack)]">
                  {messagingLimit}
                </span>
              </div>
              <div className="h-2 rounded-full bg-[#e5e7eb] overflow-hidden">
                {isUnlimited ? (
                  <div className="h-full rounded-full bg-[var(--accent-2)] w-full" />
                ) : (
                  <div
                    className="h-full rounded-full bg-[var(--accent-2)]"
                    style={{
                      width: messagingLimitData?.limit
                        ? `${Math.min(
                            100,
                            (Number(messagingLimitData.remaining || 0) /
                              Number(messagingLimitData.limit || 1)) *
                              100,
                          ).toFixed(0)}%`
                        : '0%',
                      transition: 'width 250ms ease-out',
                    }}
                  />
                )}
              </div>
              {/* {messagingLimitData?.tierName && (
                <p className="mt-1 text-[11px] text-[var(--font-600)]">
                  Tier: {messagingLimitData.tierName}
                </p>
              )} */}
            </div>
          )}
        </div>
      </div>

      {/* WhatsApp Numbers Selector */}
      <div className="border-t border-[var(--surface-border)] pt-[16px] min-h-[173.5px]">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-[var(--textBlack)]">
            WhatsApp numbers
          </p>
        </div>
        {loadingNumbers ? (
          <div className="w-full h-full flex-1 flex items-center justify-center">
            <Loader Width="24px" Height="24px" />
          </div>
        ) : whatsappNumbers.length > 0 ? (
          <WhatsAppNumberSelect
            numbers={whatsappNumbers}
            selectedNumber={selectedNumber}
            onSelect={onSelectNumber}
            loading={loadingNumbers}
          />
        ) : (
          <div className="text-gray-500 text-center h-10 flex items-center justify-center">
            No WhatsApp business numbers found
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessDetailsCard;
